import React, { useEffect, useRef, useState } from "react";
import {
  IcoClose,
  IcoList,
  IcoNext,
  IcoNext15,
  IcoPause,
  IcoPlay,
  IcoPrev,
  IcoPrev15,
} from "~/icons";
import { IChapter } from "~/utils/interface";
import { formatTimestamp, sendToDataLayer } from "~/utils/helpers";

interface IAudioPlayer {
  totalChapters: number;
  freeChapters: IChapter[];
  title: string;
  cover: string;
  id: number;
  isOpen: boolean;
  onClose: () => void;
}

const AudioPlayer = ({
  isOpen,
  onClose,
  id,
  cover,
  title,
  totalChapters,
  freeChapters,
}: IAudioPlayer) => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>();
  const handleClickOnClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const audio = audioRef.current;
    if (audio) {
      if (progress > 0) {
        audio.currentTime = progress;
        setProgress(progress);
      }
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.error("Playback error:", err));
    }
  }, [isOpen]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.load();
      setProgress(0);
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.error("Playback error:", err));
    }
  }, [currentChapter]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      await audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio) {
      setProgress(audio.currentTime);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    const newTime = parseFloat(e.target.value);
    if (audio) {
      audio.currentTime = newTime;
      setProgress(newTime);
    }
  };

  const skipTime = (seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(
        0,
        Math.min(audio.duration, audio.currentTime + seconds),
      );
      setProgress(audio.currentTime);
    }
  };

  const handleNextChapter = () => {
    if (currentChapter < 2) {
      setCurrentChapter(currentChapter + 1);
      setProgress(0);
      setIsPlaying(true);
    } else {
      const audio = audioRef.current;
      audio.pause();
      setIsPlaying(false);
      sendToDataLayer({ event: "open_checkout" });
      window.open(`${import.meta.env.VITE_PAYWALL_URL}`, "_blank");
    }
  };

  const handlePrevChapter = () => {
    if (currentChapter > 0) {
      setCurrentChapter(currentChapter - 1);
      setProgress(0);
      setIsPlaying(false);
    }
  };

  const progressPercentage = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <>
      {isOpen && (
        <div className="flex justify-center fixed top-0 left-0 w-full h-full z-30">
          <div
            className="fixed w-full h-full left-0 top-0 bg-black opacity-80"
            onClick={handleClickOnClose}
          />
          <div className="max-w-full w-[375px] z-40 h-full bg-black">
            <div className="px-4 pt-[90px] h-full bg-[radial-gradient(circle,rgba(9,18,39,0)_0%,rgba(9,18,39,0.56)_58%,rgba(9,18,39,0.88)_75%,rgba(9,18,39,0.93)_85%,rgba(9,18,39,0.97)_92%,rgba(9,18,39,1)_100%)] backdrop-blur-[300px]">
              <button
                onClick={handleClickOnClose}
                className="absolute right-6 top-6"
              >
                <IcoClose width={20} height={20} />
              </button>
              <div>
                <img
                  width={272}
                  height={272}
                  src={`https://static.wromance.com/${id}/${cover}`}
                  alt={title}
                  className="mx-auto rounded-2xl"
                />
              </div>
              <div className="flex justify-center items-center rounded-[40px] bg-[#335495] mt-12 p-3 font-semibold leading-[22px] text-[16px] mb-10 text-white">
                <IcoList width={24} height={24} className="mr-2" />
                Chapter {currentChapter + 1} of {totalChapters}
              </div>
              <div>
                <audio
                  ref={audioRef}
                  src={`https://static.wromance.com/${id}/${freeChapters[currentChapter].audio}`}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={() =>
                    setDuration(audioRef.current?.duration || 0)
                  }
                  onEnded={handleNextChapter}
                />
                <div className="w-full">
                  <div
                    className="relative w-full bg-transparent rounded-[3px]"
                    style={
                      {
                        "--seek-before-width": `${progressPercentage}%`,
                      } as React.CSSProperties
                    }
                  >
                    <input
                      type="range"
                      min="0"
                      max={duration}
                      value={progress}
                      onChange={handleSeek}
                      className="w-full appearance-none bg-gray-700 cursor-pointer relative z-2 [&::-webkit-slider-runnable-track]:w-full [&::-webkit-slider-runnable-track]:h-[3px] [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-[21px] [&::-webkit-slider-thumb]:w-[21px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:mt-[-9px] [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:h-[21px] [&::-moz-range-thumb]:w-[21px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-md before:absolute before:content-[''] before:top-0 before:left-0 before:w-[var(--seek-before-width)] before:h-[3px] before:bg-white before:cursor-pointer"
                    />
                  </div>

                  <div className="mt-3 flex justify-between font-medium text-[11px] mb-9 text-white">
                    <span>{formatTimestamp(progress)}</span>
                    <span>{formatTimestamp(duration)}</span>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={handlePrevChapter}
                    disabled={currentChapter === 0}
                  >
                    <IcoPrev width={24} height={24} />
                  </button>
                  <button onClick={() => skipTime(-15)}>
                    <IcoPrev15 width={42} height={42} />
                  </button>
                  <button
                    className="w-[70px] h-[70px] flex justify-center items-center bg-white rounded-full"
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? (
                      <IcoPause width={32} height={32} />
                    ) : (
                      <IcoPlay width={32} height={32} />
                    )}
                  </button>
                  <button onClick={() => skipTime(15)}>
                    <IcoNext15 width={42} height={42} />
                  </button>
                  <button onClick={handleNextChapter}>
                    <IcoNext width={24} height={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AudioPlayer.displayName = "AudioPlayer";

export default AudioPlayer;
